import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Filter,
  RefreshCw,
  Eye,
  RotateCcw,
  Monitor,
  Server,
} from 'lucide-react';
import { useSystemEvents } from '../../../hooks/platform-admin/useSystemEvents';
import { SystemEvent } from '../../../services/platform-admin/system-events.service';
import ClientErrorLog from '../../../components/platform-admin/ClientErrorLog';

export default function SystemEventsPage() {
  const [viewMode, setViewMode] = useState<'server' | 'client'>('server');
  const { getStats, getEvents, retryEvent, isLoading, error } = useSystemEvents();

  const [events, setEvents] = useState<SystemEvent[]>([]);
  const [stats, setStats] = useState<{
    total: number;
    byStatus: Record<string, number>;
    bySeverity: Record<string, number>;
    criticalLast24h: number;
  }>({
    total: 0,
    byStatus: {},
    bySeverity: {},
    criticalLast24h: 0,
  });
  const [total, setTotal] = useState(0);

  const [eventTypeFilter, setEventTypeFilter] = useState<string>('');
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);

  const loadStats = async () => {
    const result = await getStats();
    if (result) {
      setStats({
        total: result.total || 0,
        byStatus: result.byStatus || {},
        bySeverity: result.bySeverity || {},
        criticalLast24h: result.criticalLast24h || 0,
      });
    }
  };

  const loadEvents = async () => {
    const result = await getEvents({
      eventType: eventTypeFilter || undefined,
      severity: severityFilter || undefined,
      status: statusFilter || undefined,
      skip: (currentPage - 1) * pageSize,
      take: pageSize,
    });

    if (result) {
      setEvents(result.data);
      setTotal(result.total);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    loadEvents();
  }, [eventTypeFilter, severityFilter, statusFilter, currentPage]);

  const handleRetry = async (event: SystemEvent) => {
    if (window.confirm(`Retry event "${event.operation}"?`)) {
      const result = await retryEvent(event.id);
      if (result) {
        await loadEvents();
        await loadStats();
      }
    }
  };

  const getSeverityBadge = (severity: SystemEvent['severity']) => {
    const colors = {
      LOW: 'bg-blue-100 text-blue-800',
      MEDIUM: 'bg-yellow-100 text-yellow-800',
      HIGH: 'bg-orange-100 text-orange-800',
      CRITICAL: 'bg-red-100 text-red-800',
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[severity]}`}>
        {severity}
      </span>
    );
  };

  const getStatusBadge = (status: SystemEvent['status']) => {
    const colors = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      PROCESSING: 'bg-blue-100 text-blue-800',
      RESOLVED: 'bg-green-100 text-green-800',
      FAILED: 'bg-red-100 text-red-800',
      IGNORED: 'bg-gray-100 text-gray-800',
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status]}`}>
        {status}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Events & Error Logs</h1>
          <p className="text-sm text-gray-600 mt-1">
            Monitor server events and client-side errors
          </p>
        </div>
        <button
          onClick={() => {
            loadEvents();
            loadStats();
          }}
          className="flex items-center gap-2 bg-gradient-to-r from-orange-600 to-red-600 text-white px-4 py-2 rounded-lg hover:from-orange-700 hover:to-red-700 transition-all"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* View Mode Tabs */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-1 inline-flex">
        <button
          onClick={() => setViewMode('server')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
            viewMode === 'server'
              ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Server className="h-4 w-4" />
          Server Events
        </button>
        <button
          onClick={() => setViewMode('client')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
            viewMode === 'client'
              ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Monitor className="h-4 w-4" />
          Client Errors
        </button>
      </div>

      {/* Conditional Content Based on View Mode */}
      {viewMode === 'client' ? (
        /* Client Error Log View */
        <ClientErrorLog showStats={true} autoRefresh={true} />
      ) : (
        /* Server Events View */
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
              <div className="text-sm text-gray-600">Total Events</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
              <div className="text-sm text-gray-600">Pending</div>
              <div className="text-2xl font-bold text-yellow-600 mt-1">{stats.byStatus?.PENDING || 0}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
              <div className="text-sm text-gray-600">Resolved</div>
              <div className="text-2xl font-bold text-green-600 mt-1">{stats.byStatus?.RESOLVED || 0}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
              <div className="text-sm text-gray-600">Failed</div>
              <div className="text-2xl font-bold text-red-600 mt-1">{stats.byStatus?.FAILED || 0}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
              <div className="text-sm text-gray-600">Ignored</div>
              <div className="text-2xl font-bold text-gray-600 mt-1">{stats.byStatus?.IGNORED || 0}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border border-gray-200 border-l-4 border-l-red-500">
              <div className="text-sm text-gray-600">Critical (24h)</div>
              <div className="text-2xl font-bold text-red-600 mt-1">{stats.criticalLast24h}</div>
            </div>
          </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={eventTypeFilter}
              onChange={(e) => {
                setEventTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="">All Event Types</option>
              <option value="WEBHOOK_FAILURE">Webhook Failure</option>
              <option value="INTEGRATION_SYNC_FAILURE">Integration Sync Failure</option>
              <option value="SCHEDULED_JOB_FAILURE">Scheduled Job Failure</option>
              <option value="API_ERROR">API Error</option>
              <option value="RATE_LIMIT_EXCEEDED">Rate Limit Exceeded</option>
              <option value="DATABASE_ERROR">Database Error</option>
              <option value="EXTERNAL_SERVICE_ERROR">External Service Error</option>
              <option value="AUTHENTICATION_FAILURE">Authentication Failure</option>
            </select>
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={severityFilter}
              onChange={(e) => {
                setSeverityFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="">All Severities</option>
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
              <option value="CRITICAL">Critical</option>
            </select>
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="PROCESSING">Processing</option>
              <option value="RESOLVED">Resolved</option>
              <option value="FAILED">Failed</option>
              <option value="IGNORED">Ignored</option>
            </select>
          </div>

          <button
            onClick={() => {
              setEventTypeFilter('');
              setSeverityFilter('');
              setStatusFilter('');
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Events Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Event Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Operation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Severity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Error Message
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Retries
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    Loading events...
                  </td>
                </tr>
              ) : events.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    No events found
                  </td>
                </tr>
              ) : (
                events.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(event.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {event.eventType.replace(/_/g, ' ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="max-w-xs truncate">{event.operation}</div>
                      {event.organization && (
                        <div className="text-xs text-gray-500">{event.organization.name}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getSeverityBadge(event.severity)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(event.status)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="max-w-md truncate">
                        {event.errorMessage || 'No error message'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {event.retryCount} / {event.maxRetries}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/platform-admin/events/${event.id}`}
                          className="text-orange-600 hover:text-orange-900"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        {event.status === 'PENDING' && event.retryCount < event.maxRetries && (
                          <button
                            onClick={() => handleRetry(event)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Retry Now"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {(currentPage - 1) * pageSize + 1} to{' '}
              {Math.min(currentPage * pageSize, total)} of {total} results
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
}
